import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import { DateField } from '@components/ui/DateField/DateField';
import { formatYmd } from '@utils/dateUtils';
import type { IWorkingHours } from '@interfaces/IWorkingHours';
import { recentWorkingDays, REPLAY_LOOKBACK_DAYS } from './replayDays';
import styles from './ReplayDayPicker.module.css';

export interface IReplayDayPickerProps {
  /** The site's current moment; its zone defines "today". */
  now: DateTime;
  /** The selected day, `YYYY-MM-DD`. */
  day: string;
  workingHours: IWorkingHours | undefined;
  onPick(date: string): void;
}

/** A site-local calendar day as a browser-local Date, which is what the calendar compares. */
const asLocalDate = (date: string) => DateTime.fromISO(date).toJSDate();

/** Today and the two previous working days as pills, plus a calendar for anything within the lookback. */
export function ReplayDayPicker({ now, day, workingHours, onPick }: IReplayDayPickerProps) {
  const { t, i18n } = useTranslation();
  const today = formatYmd(now, now.zone.name);
  const dayLabel = (date: string) => {
    const parsed = DateTime.fromISO(date).setLocale(i18n.language);
    return t('monitor.replay.dayPill', { weekday: parsed.toFormat('ccc'), day: parsed.day });
  };
  const options = [
    { id: today, label: t('monitor.replay.today') },
    ...recentWorkingDays(now, workingHours).map((date) => ({ id: date, label: dayLabel(date) })),
  ];

  return (
    <div className={styles.days}>
      <SegmentedControl size="sm" ariaLabel={t('monitor.replay.daysLabel')} value={day} onChange={onPick} options={options} />
      <DateField
        variant="icon"
        elevated
        value={asLocalDate(day)}
        onChange={(picked) => onPick(formatYmd(picked, DateTime.local().zone.name))}
        placeholder={t('monitor.replay.pickDate')}
        ariaLabel={t('monitor.replay.pickDate')}
        minDate={asLocalDate(formatYmd(now.minus({ days: REPLAY_LOOKBACK_DAYS }), now.zone.name))}
        maxDate={asLocalDate(today)}
        today={asLocalDate(today)}
      />
    </div>
  );
}
